import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useShortcutSubContext } from "@/context/ShortcutContext";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PlusIcon } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { CreateInvoiceSheet } from "@/pages/Facility/billing/account/components/CreateInvoiceSheet";
import AddMultipleChargeItemsSheet from "@/pages/Facility/services/serviceRequests/components/AddMultipleChargeItemsSheet";
import { ChargeItemCard } from "@/pages/Facility/services/serviceRequests/components/ChargeItemCard";

import { getPermissions } from "@/common/Permissions";
import { usePermissions } from "@/context/PermissionContext";
import { useOpenChangeWithConfirmation } from "@/hooks/useOpenChangeWithConfirmation";
import useCurrentFacility from "@/pages/Facility/utils/useCurrentFacility";
import { ResourceCategorySubType } from "@/types/base/resourceCategory/resourceCategory";
import {
  AccountBillingStatus,
  AccountStatus,
} from "@/types/billing/account/Account";
import accountApi from "@/types/billing/account/accountApi";
import {
  ChargeItemRead,
  ChargeItemServiceResource,
  ChargeItemStatus,
} from "@/types/billing/chargeItem/chargeItem";
import chargeItemApi from "@/types/billing/chargeItem/chargeItemApi";
import { InvoiceStatus } from "@/types/billing/invoice/invoice";
import invoiceApi from "@/types/billing/invoice/invoiceApi";
import { ShortcutBadge } from "@/Utils/keyboardShortcutComponents";
import mutate from "@/Utils/request/mutate";
import query from "@/Utils/request/query";
import { navigate } from "raviger";

interface ChargeItemsSectionProps {
  facilityId: string;
  resourceId: string;
  patientId?: string;
  serviceResourceType: ChargeItemServiceResource;
  sourceUrl?: string;
  encounterId?: string;
  disableCreateChargeItems?: boolean;
  disableCreateChargeItemsSection?: boolean;
  viewOnly?: boolean;
  autoRefreshIntervalMs?: number;
}

export function ChargeItemsSection({
  facilityId,
  resourceId,
  patientId,
  serviceResourceType,
  sourceUrl,
  encounterId,
  disableCreateChargeItems = false,
  disableCreateChargeItemsSection = false,
  viewOnly = false,
  autoRefreshIntervalMs,
}: ChargeItemsSectionProps) {
  const { t } = useTranslation();
  useShortcutSubContext("facility:appointment");
  const queryClient = useQueryClient();
  const { facility } = useCurrentFacility();
  const { hasPermission } = usePermissions();
  const { canCreateChargeItem, canWriteInvoice } = getPermissions(
    hasPermission,
    facility?.permissions ?? [],
  );

  const [isMultiAddOpen, setIsMultiAddOpen] = useState(false);
  const [invoiceSheetState, setInvoiceSheetState] = useState<{
    open: boolean;
    chargeItems: ChargeItemRead[];
  }>({
    open: false,
    chargeItems: [],
  });

  const [hasSelectedItems, setHasSelectedItems] = useState(false);

  const { data: chargeItems } = useQuery({
    queryKey: ["chargeItems", facilityId, resourceId],
    queryFn: query(chargeItemApi.listChargeItem, {
      pathParams: {
        facilityId: facilityId,
      },
      queryParams: {
        service_resource: serviceResourceType,
        service_resource_id: resourceId,
      },
    }),
    enabled: !!resourceId,
    refetchInterval: autoRefreshIntervalMs,
    refetchIntervalInBackground: true,
  });

  const { data: account } = useQuery({
    queryKey: ["accounts", patientId],
    queryFn: query(accountApi.listAccount, {
      pathParams: { facilityId },
      queryParams: {
        patient: patientId,
        limit: 1,
        offset: 0,
        status: AccountStatus.active,
        billing_status: AccountBillingStatus.open,
      },
    }),
    enabled: Boolean(patientId),
    refetchInterval: autoRefreshIntervalMs,
    refetchIntervalInBackground: true,
  });

  const { mutateAsync: createInvoiceMutation, isPending: isCreatingInvoice } =
    useMutation({
      mutationFn: mutate(invoiceApi.createInvoice, {
        pathParams: { facilityId },
      }),
    });

  const buildInvoiceUrl = (invoiceId: string, chargeItemId?: string) => {
    const params = new URLSearchParams();
    if (sourceUrl) {
      params.set("sourceUrl", sourceUrl);
    }
    if (chargeItemId) {
      params.set("editChargeItems", chargeItemId);
    }
    return `/facility/${facilityId}/billing/invoices/${invoiceId}${params.size > 0 ? `?${params.toString()}` : ""}`;
  };

  const buildCreateInvoiceUrl = (accountId: string, chargeItemId?: string) => {
    const params = new URLSearchParams();
    if (sourceUrl) {
      params.set("sourceUrl", sourceUrl);
    }
    if (chargeItemId) {
      params.set("preselectChargeItems", chargeItemId);
      params.set("editChargeItems", chargeItemId);
    }
    return `/facility/${facilityId}/billing/account/${accountId}/invoices/create${params.size > 0 ? `?${params.toString()}` : ""}`;
  };

  const handleDiscountChargeItem = async (chargeItem: ChargeItemRead) => {
    if (isCreatingInvoice) {
      return;
    }

    if (
      chargeItem.paid_invoice?.status === InvoiceStatus.balanced ||
      chargeItem.paid_invoice?.status === InvoiceStatus.issued
    ) {
      return;
    }

    if (chargeItem.paid_invoice?.id) {
      navigate(buildInvoiceUrl(chargeItem.paid_invoice.id, chargeItem.id));
      return;
    }

    const accountId = chargeItem.account ?? account?.results[0]?.id ?? null;

    if (!accountId) {
      navigate(
        `/facility/${facilityId}/billing/account?status=active&patient_filter=${patientId}`,
      );
      return;
    }

    try {
      const invoice = await createInvoiceMutation({
        account: accountId,
        charge_items: [chargeItem.id],
        status: InvoiceStatus.draft,
      });
      navigate(buildInvoiceUrl(invoice.id, chargeItem.id));
    } catch {
      navigate(buildCreateInvoiceUrl(accountId, chargeItem.id));
    }
  };

  const handleOpenChange = useOpenChangeWithConfirmation({
    isDirty: hasSelectedItems,
    setOpen: setIsMultiAddOpen,
  });

  if (viewOnly && chargeItems?.results.length === 0) {
    return null;
  }

  const handleSuccessfulSaveInMutlipleChargeItems = () => {
    setHasSelectedItems(false);
    setIsMultiAddOpen(false);
  };

  return (
    <>
      <Card className="bg-white shadow-sm rounded-md p-1">
        <CardHeader className="p-2 bg-gray-50">
          <div className="flex flex-col sm:flex-row gap-2 items-center justify-between">
            <CardTitle>{t("charge_items")}</CardTitle>
            <div className="flex items-center gap-2">
              {canWriteInvoice &&
                (chargeItems?.results ?? []).filter(
                  (chargeItem) =>
                    chargeItem.status === ChargeItemStatus.billable,
                ).length > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setInvoiceSheetState({
                        open: true,
                        chargeItems:
                          chargeItems?.results.filter(
                            (chargeItem) =>
                              chargeItem.status === ChargeItemStatus.billable,
                          ) ?? [],
                      })
                    }
                  >
                    <PlusIcon className="size-4 mr-2" />
                    {t("create_invoice")}
                    <ShortcutBadge actionId="create-an-invoice" />
                  </Button>
                )}
              {!disableCreateChargeItemsSection &&
                !viewOnly &&
                canCreateChargeItem && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsMultiAddOpen(true)}
                  >
                    <PlusIcon className="size-4 mr-2" />
                    {t("add_charge_items")}
                    <ShortcutBadge actionId="add-a-charge-item" />
                  </Button>
                )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4 p-2 px-1">
          {chargeItems?.results.map((chargeItem) => (
            <ChargeItemCard
              key={chargeItem.id}
              chargeItem={chargeItem}
              sourceUrl={sourceUrl}
              onDiscount={
                canWriteInvoice &&
                serviceResourceType === ChargeItemServiceResource.appointment
                  ? handleDiscountChargeItem
                  : undefined
              }
            />
          ))}
        </CardContent>
      </Card>

      {/* Add the sheets for invoice creation and charge items */}
      {invoiceSheetState.open && (
        <CreateInvoiceSheet
          facilityId={facilityId}
          accountId={account?.results[0]?.id ?? ""}
          open={invoiceSheetState.open}
          disableCreateChargeItems={disableCreateChargeItems}
          onOpenChange={() =>
            setInvoiceSheetState({ open: false, chargeItems: [] })
          }
          preSelectedChargeItems={invoiceSheetState.chargeItems}
          onSuccess={() => {
            queryClient.invalidateQueries({
              queryKey: ["chargeItems", facilityId, resourceId],
            });
            setInvoiceSheetState({ open: false, chargeItems: [] });
          }}
          sourceUrl={sourceUrl}
        />
      )}

      {canCreateChargeItem && (
        <AddMultipleChargeItemsSheet
          open={isMultiAddOpen}
          onOpenChange={handleOpenChange}
          facilityId={facilityId}
          serviceResourceId={resourceId}
          patientId={patientId}
          encounterId={encounterId}
          serviceResourceType={serviceResourceType}
          onChargeItemsAdded={() => {
            queryClient.invalidateQueries({
              queryKey: ["chargeItems", facilityId, resourceId],
            });
          }}
          resourceSubType={ResourceCategorySubType.other}
          onHasSelectedItemsChange={(value) => {
            setHasSelectedItems(value);
          }}
          onSuccessfulSave={handleSuccessfulSaveInMutlipleChargeItems}
        />
      )}
    </>
  );
}
